import { ALL } from '@components/reports/specializedFilterFields'
import { accountTypesPart } from '@components/reports/reportFilterSummary'

/**
 * CC-53890 (spec 128) — the General Ledger's account scope: what the page SENDS,
 * what it REFUSES to send, and how it DESCRIBES the scope in an export.
 *
 * A plain module, not the page, so the rules are unit-testable on their own and
 * the component file only exports a component (react-refresh).
 *
 * ## The two scopes, and the one rule between them
 *
 *   Account Types  a TOKEN (CB AccountCategories ints), resolved to the current
 *                  member accounts by cb-api on EVERY run (CC-50326) — so "all
 *                  expenses" still means all expenses after a new one is added.
 *   Accounts       explicit ids — frozen by design: "these two accounts".
 *
 * The page never sends both. Picking specific accounts clears the types, and
 * picking a type resets the accounts to All (see the two fields' `set`). If a
 * stored filter object carries both anyway (hand-edited, or older than this
 * rule), `toGlWire` sends the ids alone — never the token — and cb-api's own
 * backstop would intersect them if both ever arrived.
 */

/**
 * CB's `AccountCategories` values. Bank (7) and Trust (9) joined in CC-53890 —
 * Robin's "show me Bank" — with cb-api's overlap rule (a SET of ids, so Asset +
 * Bank is just Asset) stated in cb-api/src/domains/reports/accountScope.js.
 */
export const ACCOUNT_TYPE_OPTIONS = [
  { value: '1', label: 'Asset' },
  { value: '7', label: 'Bank' },
  { value: '9', label: 'Trust' },
  { value: '2', label: 'Liability' },
  { value: '6', label: 'Equity' },
  { value: '3', label: 'Income' },
  { value: '4', label: 'Expense' },
]
export const ACCOUNT_TYPE_LABEL = new Map(ACCOUNT_TYPE_OPTIONS.map((o) => [o.value, o.label]))

export const NONE_SELECTED_MESSAGE =
  'No accounts are selected, so the General Ledger was not run. Choose at least one account, or tick All to include every account.'
export const INVALID_SELECTION_MESSAGE =
  'The saved account selection is not a list of accounts, so the General Ledger was not run. Choose the accounts again.'
export const allStaleMessage = (n) =>
  `${n === 1 ? 'The selected account is' : `None of the ${n} selected accounts is`} in the chart of accounts available to you, so the General Ledger was not run. ${n === 1 ? 'It' : 'They'} may have been deleted, or you may not have access — choose the accounts again.`
export const someStaleMessage = (stale, total) =>
  `${stale} of the ${total} selected accounts ${stale === 1 ? 'is' : 'are'} not in the chart of accounts available to you — ${stale === 1 ? 'it' : 'they'} may have been deleted, or you may not have access. ${stale === 1 ? 'It stays' : 'They stay'} in the selection, and the General Ledger shows nothing for ${stale === 1 ? 'it' : 'them'}.`
export const CHART_UNREADABLE_MESSAGE =
  'The chart of accounts could not be loaded, so the selected accounts could not be checked or named.'

// The server's rule (cb-api accountScope.js), mirrored so the page refuses
// BEFORE a request rather than after a 422. The server still decides.
const ACCOUNT_ID_RE = /^-?\d+$/

/** The explicit ids in an account selection ([] for All and for None). */
export const selectedAccountIds = (selection) =>
  selection != null && selection !== ALL ? String(selection).split('|').filter(Boolean) : []

/** Whether a selection names specific accounts (as opposed to All / None). */
export const isSpecificSelection = (selection) => selectedAccountIds(selection).length > 0

/**
 * The GL wire, or the reason there is none.
 *
 * @returns {{ wire: object|null, refusal: string|null }}
 */
export const toGlWire = (f = {}) => {
  const base = {
    startDate: f.startDate,
    endDate: f.endDate,
    isCashBasis: f.isCashBasis,
    showZeroBal: f.showZeroBal,
    showCurrSymbol: f.showCurrSymbol,
  }
  const sel = f.accountSelection
  // '' is NONE selected. Sending it — or sending nothing in its place — would be
  // ALL accounts at CB, so there is no wire at all.
  if (sel === '') return { wire: null, refusal: NONE_SELECTED_MESSAGE }
  if (sel != null && sel !== ALL) {
    const ids = selectedAccountIds(sel)
    if (ids.length === 0 || ids.some((id) => !ACCOUNT_ID_RE.test(id))) {
      return { wire: null, refusal: INVALID_SELECTION_MESSAGE }
    }
    // CB's own spelling, as ONE pipe-joined string. NOT `filteredAccountIds`
    // (the ViewResult's name — binds to nothing on this action and runs every
    // account, measured), and never an array (axios would send
    // `filteredAccounts[]=…`, which also binds to nothing). No type token
    // alongside: the ids ARE the scope.
    return { wire: { ...base, filteredAccounts: ids.join('|') }, refusal: null }
  }
  // CC-50326: the token travels on the wire; cb-api swaps it for the current
  // member ids under CB's own `filteredAccounts` key.
  return { wire: { ...base, accountCategories: f.accountCategories || '' }, refusal: null }
}

/** Selected ids that are NOT in the loaded chart of accounts (options from accountPickerOptions). */
export const staleAccountIds = (selection, options) => {
  const known = new Set((options || []).map((o) => String(o.key)))
  return selectedAccountIds(selection).filter((id) => !known.has(id))
}

/**
 * The scope line for the export masthead / print header (CC-50029 family).
 *
 * Neither CB's GL export header nor cb-api's CSV stamp NAMES the accounts (the
 * stamp gives a count), so a two-account General Ledger would otherwise read
 * like a complete one. Names come from the same option builder the picker uses,
 * so the export says exactly what the user ticked.
 *
 * @param {object} f           the page's filter object
 * @param {Map|null} labelsById account id → picker label; null when the chart
 *                              has not loaded (or could not be read)
 */
export const accountScopePart = (f = {}, labelsById = null) => {
  const ids = selectedAccountIds(f.accountSelection)
  if (f.accountSelection === '') return 'Accounts: none selected'
  if (ids.length === 0) return accountTypesPart(f.accountCategories, ACCOUNT_TYPE_LABEL)
  if (!labelsById) return `Accounts: ${ids.length} selected account${ids.length === 1 ? '' : 's'} (names unavailable)`
  const names = ids.map((id) => labelsById.get(id)).filter(Boolean)
  const missing = ids.length - names.length
  const tail = missing ? `${names.length ? ', and ' : ''}${missing} account${missing === 1 ? '' : 's'} no longer in the chart` : ''
  return `Accounts: ${names.join(', ')}${tail}`
}
